<template>
  <div>
    <!-- Header -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-pencil</v-icon>
            Edit DG Billing Record
            <v-spacer />
            <v-chip
              v-if="originalRecord"
              color="info"
              text-color="white"
              class="mr-2"
            >
              <v-icon left small>mdi-identifier</v-icon>
              ID: {{ originalRecord._id || originalRecord.id }}
            </v-chip>
          </v-card-title>
          <v-card-text>
            <div v-if="originalRecord" class="mb-2">
              <strong>Original DG Record:</strong>
              Created on {{ formatDateTime(originalRecord.createdAt) }}
              <span v-if="originalRecord.updatedAt">
                | Last modified: {{ formatDateTime(originalRecord.updatedAt) }}
              </span>
            </div>
            <p>
              Modify the DG billing record details below.
              All changes will be validated before saving.
            </p>

            <!-- Progress Stepper -->
            <v-stepper v-model="currentStep" class="mt-4">
              <v-stepper-header>
                <v-stepper-step :complete="currentStep > 1" step="1">
                  Update Period & DG Readings
                </v-stepper-step>
                <v-divider />
                <v-stepper-step :complete="currentStep > 2" step="2">
                  Update Diesel & Hours
                </v-stepper-step>
                <v-divider />
                <v-stepper-step :complete="currentStep > 3" step="3">
                  Recalculate Efficiency
                </v-stepper-step>
                <v-divider />
                <v-stepper-step :complete="currentStep > 4" step="4">
                  Review & Save Changes
                </v-stepper-step>
              </v-stepper-header>
            </v-stepper>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row v-if="loading && !originalRecord" class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-text class="text-center">
            <v-progress-circular indeterminate color="primary" size="64" />
            <div class="mt-4">Loading DG billing record...</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-row v-if="error" class="mt-4">
      <v-col cols="12">
        <v-alert type="error" dismissible @input="clearError">
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>

    <!-- Step 1: DG Period Selection -->
    <v-row class="mt-4" v-if="currentStep === 1 && originalRecord">
      <v-col cols="12">
        <DGBillingPeriodSelector
          ref="dgPeriodSelector"
          :edit-mode="true"
          :initial-data="dgPeriodSelectorData"
          :is-d-g-mode="true"
          @dg-consumption-data-retrieved="onDGConsumptionDataRetrieved"
          @consumption-data-retrieved="onDGConsumptionDataRetrieved"
          @show-message="showMessage"
        />

        <v-card-actions class="justify-end mt-4">
          <v-btn @click="cancelEdit" text>Cancel</v-btn>
          <v-btn
            @click="nextStep"
            color="primary"
            :disabled="!dgConsumptionData"
          >
            Next: Diesel & Hours
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>

    <!-- Step 2: Diesel and Running Hours Entry -->
    <v-row class="mt-4" v-if="currentStep === 2">
      <v-col cols="12">
        <DGManualDataEntryForm
          ref="dgManualDataForm"
          :edit-mode="true"
          :initial-data="dgManualDataFormData"
          :dg-consumption-data="dgConsumptionData"
          :is-d-g-mode="true"
          @data-updated="onDGManualDataUpdated"
          @show-message="showMessage"
        />

        <v-card-actions class="justify-end mt-4">
          <v-btn @click="cancelEdit" text>Cancel</v-btn>
          <v-btn @click="previousStep" text>
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-btn
            @click="nextStep"
            color="primary"
            :disabled="!dgManualData.dieselConsumption || !dgManualData.runningHours"
          >
            Next: Calculate Efficiency
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>

    <!-- Step 3: Efficiency Calculation -->
    <v-row class="mt-4" v-if="currentStep === 3 && originalRecord">
      <v-col cols="12">
        <DGEfficiencyMetrics
          v-if="calculatedEfficiency"
          :dg-consumption="dgConsumptionData ? dgConsumptionData.totalConsumption : 0"
          :diesel-consumption="dgManualData.dieselConsumption"
          :running-hours="dgManualData.runningHours"
        />

        <v-card-actions class="justify-end mt-4">
          <v-btn @click="cancelEdit" text>Cancel</v-btn>
          <v-btn @click="previousStep" text>
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-btn
            @click="nextStep"
            color="primary"
            :disabled="!calculatedEfficiency"
          >
            Next: Review
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>

    <!-- Step 4: Review & Save -->
    <v-row class="mt-4" v-if="currentStep === 4 && originalRecord">
      <v-col cols="12">
        <DGBillSaveComponent
          ref="dgSaveComponent"
          :edit-mode="true"
          :original-record="originalRecord"
          :updated-record="updatedDGRecord"
          :dg-consumption-data="dgConsumptionData"
          :manual-data="dgManualData"
          :efficiency-metrics="calculatedEfficiency"
          @save-success="onSaveSuccess"
          @save-error="onSaveError"
          @show-message="showMessage"
        />

        <v-card-actions class="justify-end mt-4">
          <v-btn @click="cancelEdit" text>Cancel</v-btn>
          <v-btn @click="previousStep" text>
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>

    <!-- Success Dialog -->
    <v-dialog v-model="showSuccessDialog" max-width="500">
      <v-card>
        <v-card-title class="success--text">
          <v-icon left color="success">mdi-check-circle</v-icon>
          DG Record Updated Successfully
        </v-card-title>
        <v-card-text>
          <p>The DG billing record has been updated successfully.</p>
          <div class="mt-2" v-if="originalRecord">
            <strong>Record ID:</strong> {{ originalRecord._id || originalRecord.id }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="goToDetail">
            View Updated Record
          </v-btn>
          <v-btn text @click="goToHistory">
            Back to History
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
// Use existing DG components - we'll create the missing ones or use alternatives
import DGEfficiencyMetrics from '../components/dg/DGEfficiencyMetrics.vue';
// For now, we'll use the regular billing components as placeholders
import BillingPeriodSelector from '../components/billing/BillingPeriodSelector.vue';
import ManualDataEntryForm from '../components/billing/ManualDataEntryForm.vue';
import DGBillSaveComponent from '../components/dg/DGBillSaveComponent.vue';

export default {
  name: 'DGBillingEdit',

  components: {
    DGEfficiencyMetrics,
    // Use regular billing components for now
    DGBillingPeriodSelector: BillingPeriodSelector,
    DGManualDataEntryForm: ManualDataEntryForm,
    DGBillSaveComponent,
  },

  props: {
    id: {
      type: String,
      required: true,
      validator(value) {
        return value && value.trim().length > 0;
      },
    },
  },

  data() {
    return {
      currentStep: 1,
      originalRecord: null,
      dgConsumptionData: null,
      dgManualData: {
        dieselConsumption: null,
        runningHours: null,
      },
      calculatedEfficiency: null,
      showSuccessDialog: false,
    };
  },

  computed: {
    ...mapState('dgBilling', ['loading', 'error']),
    ...mapGetters('dgBilling', ['isEditMode']),

    dgPeriodSelectorData() {
      if (!this.originalRecord || !this.originalRecord.dgConsumptionData) return null;

      return {
        _id: this.originalRecord._id || this.originalRecord.id,
        billingPeriod: this.originalRecord.billingPeriod,
        // Map DG consumption data to the format expected by BillingPeriodSelector
        consumptionData: {
          totalConsumption: this.originalRecord.dgConsumptionData.totalConsumption || 0,
          initialReading: this.originalRecord.dgConsumptionData.initialReading || 0,
          finalReading: this.originalRecord.dgConsumptionData.finalReading || 0,
          billingPeriod: this.originalRecord.billingPeriod,
        },
        // Pass DG mode flag to the component
        isDGMode: true,
      };
    },

    dgManualDataFormData() {
      if (!this.originalRecord) return null;

      return {
        // Map DG manual data to the format expected by ManualDataEntryForm
        dieselConsumption: this.originalRecord.dieselConsumption || 0,
        runningHours: this.originalRecord.runningHours || 0,
        notes: this.originalRecord.notes || '',
        // Pass DG mode flag to the component
        isDGMode: true,
      };
    },

    updatedDGRecord() {
      if (!this.originalRecord || !this.calculatedEfficiency) return null;

      return {
        ...this.originalRecord,
        billingPeriod: this.dgConsumptionData?.billingPeriod || this.originalRecord.billingPeriod,
        dgConsumptionData: this.dgConsumptionData || this.originalRecord.dgConsumptionData,
        dieselConsumption: this.dgManualData.dieselConsumption,
        runningHours: this.dgManualData.runningHours,
        efficiencyMetrics: this.calculatedEfficiency,
        updatedAt: new Date(),
      };
    },
  },

  async created() {
    await this.loadRecord();
  },

  methods: {
    ...mapActions('dgBilling', [
      'fetchDGBillingRecordForEdit',
      'updateDGBillingRecord',
      'clearError',
    ]),

    async loadRecord() {
      // Validate ID prop
      if (!this.id || this.id.trim() === '') {
        console.error('Invalid DG billing record ID:', this.id);
        this.$router.push('/dg-billing-history');
        this.showMessage({
          type: 'error',
          message: 'Invalid DG billing record ID',
        });
        return;
      }

      try {
        const record = await this.fetchDGBillingRecordForEdit(this.id);
        if (!record) {
          console.error('DG billing record not found for ID:', this.id);
          this.$router.push('/dg-billing-history');
          this.showMessage({
            type: 'error',
            message: 'DG billing record not found',
          });
          return;
        }

        this.originalRecord = record;

        // Pre-populate data for editing with null checks and proper parsing
        this.dgConsumptionData = record.dgConsumptionData || null;

        this.dgManualData = {
          dieselConsumption: parseFloat(record.dieselConsumption) || 0,
          runningHours: parseFloat(record.runningHours) || 0,
        };

        // Only calculate initial efficiency if we have valid data
        if (this.dgConsumptionData
            && this.dgConsumptionData.totalConsumption > 0
            && this.dgManualData.dieselConsumption > 0
            && this.dgManualData.runningHours > 0) {
          this.recalculateEfficiency();
        }
      } catch (error) {
        console.error('Error loading DG record for edit:', error);
        this.showMessage({
          type: 'error',
          message: 'Failed to load DG billing record for editing',
        });
        // Don't redirect on error, let user try again
      }
    },

    recalculateEfficiency() {
      // Validate required data before calculation
      if (!this.dgConsumptionData
          || !this.dgConsumptionData.totalConsumption
          || !this.dgManualData
          || !this.dgManualData.dieselConsumption
          || !this.dgManualData.runningHours) {
        return;
      }

      try {
        const totalConsumption = parseFloat(this.dgConsumptionData.totalConsumption) || 0;
        const dieselConsumption = parseFloat(this.dgManualData.dieselConsumption) || 0;
        const runningHours = parseFloat(this.dgManualData.runningHours) || 0;

        if (dieselConsumption <= 0 || runningHours <= 0 || totalConsumption <= 0) {
          console.warn('Invalid values for efficiency calculation:', {
            totalConsumption,
            dieselConsumption,
            runningHours,
          });
          return;
        }

        this.calculatedEfficiency = {
          kwhPerLitre: totalConsumption / dieselConsumption,
          litresPerHour: dieselConsumption / runningHours,
          kwhPerHour: totalConsumption / runningHours,
          totalConsumption,
          dieselConsumption,
          runningHours,
        };
      } catch (error) {
        console.error('Error recalculating efficiency:', error);
        this.showMessage({
          type: 'error',
          message: `Failed to recalculate efficiency metrics: ${error.message || 'Unknown error'}`,
        });
      }
    },

    onDGConsumptionDataRetrieved(data) {
      // Handle both electricity and DG data formats with null checks
      if (!data) {
        console.warn('No consumption data received');
        return;
      }

      // In DGBillingEdit component, we need to handle updates to readings
      // while preserving the data structure
      if (this.originalRecord && this.originalRecord.dgConsumptionData) {
        let updatedConsumptionData = null;

        if (data.dgConsumptionData) {
          // DG format - use the updated data
          updatedConsumptionData = data.dgConsumptionData;
        } else if (data.consumptionData) {
          // Electricity format - map to DG format with updated readings
          updatedConsumptionData = {
            totalConsumption: data.consumptionData.totalConsumption || 0,
            initialReading: data.consumptionData.initialReading || 0,
            finalReading: data.consumptionData.finalReading || 0,
            billingPeriod: (
              data.billingPeriod
              || data.consumptionData.billingPeriod
              || this.originalRecord.dgConsumptionData.billingPeriod
            ),
          };
        } else {
          // Direct data format
          updatedConsumptionData = {
            ...this.originalRecord.dgConsumptionData,
            ...data,
            billingPeriod: (
              data.billingPeriod || this.originalRecord.dgConsumptionData.billingPeriod
            ),
          };
        }

        // Update the consumption data with new readings
        this.dgConsumptionData = updatedConsumptionData;
      } else {
        // Fallback: use the retrieved data (shouldn't happen in edit mode)
        console.warn('⚠️ No original DG record found, using retrieved data');

        if (data.dgConsumptionData) {
          // DG format
          this.dgConsumptionData = data.dgConsumptionData;
        } else if (data.consumptionData) {
          // Electricity format - map to DG format
          this.dgConsumptionData = {
            totalConsumption: data.consumptionData.totalConsumption || 0,
            initialReading: data.consumptionData.initialReading || 0,
            finalReading: data.consumptionData.finalReading || 0,
            billingPeriod: data.billingPeriod || data.consumptionData.billingPeriod,
          };
        } else {
          // Direct data format
          this.dgConsumptionData = data;
        }
      }

      this.recalculateEfficiency();
    },

    onDGManualDataUpdated(data) {
      // Handle both electricity and DG data formats with null checks
      if (!data) {
        console.warn('No manual data received');
        return;
      }

      if (data.dieselConsumption !== undefined && data.runningHours !== undefined) {
        // DG format
        this.dgManualData = {
          dieselConsumption: parseFloat(data.dieselConsumption) || 0,
          runningHours: parseFloat(data.runningHours) || 0,
        };
      } else if (data.kvaDemand !== undefined && data.actualBillAmount !== undefined) {
        // Electricity format mapped to DG
        this.dgManualData = {
          dieselConsumption: parseFloat(data.kvaDemand) || 0,
          runningHours: parseFloat(data.actualBillAmount) || 0,
        };
      } else {
        // Direct assignment with safe defaults
        this.dgManualData = {
          dieselConsumption: parseFloat(data.dieselConsumption) || 0,
          runningHours: parseFloat(data.runningHours) || 0,
        };
      }
      this.recalculateEfficiency();
    },

    async onSaveSuccess() {
      this.showSuccessDialog = true;
    },

    onSaveError(error) {
      this.showMessage({
        type: 'error',
        message: error.message || 'Failed to update DG billing record',
      });
    },

    nextStep() {
      if (this.currentStep < 4) {
        this.currentStep += 1;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },

    cancelEdit() {
      this.$router.go(-1); // Go back to previous page
    },

    goToDetail() {
      if (!this.originalRecord) {
        this.goToHistory();
        return;
      }
      const recordId = this.originalRecord._id || this.originalRecord.id;
      this.$router.push(`/dg-billing-history/${recordId}`);
    },

    goToHistory() {
      this.$router.push('/dg-billing-history');
    },

    showMessage(message) {
      this.$emit('show-message', message);
    },

    formatDateTime(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString();
    },
  },
};
</script>

<style scoped>
.v-stepper {
  box-shadow: none;
}

.v-card-actions {
  padding: 16px 24px;
}
</style>
